import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { setupListeners } from '@reduxjs/toolkit/query'
import { BrowserRouter } from 'react-router-dom'
import { MotionConfig } from 'motion/react'
import { store } from './store'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import { ThemeProvider } from './components/ThemeProvider'
import { initAutoInstrumentation, initGlobalErrorTracking, trackError } from './lib/analytics'
import { captureAdAttribution } from './lib/attribution'
import { initFullStory } from './lib/fullstory'
import { initGtag } from './lib/gtag'
import { isSessionMonitoringOrigin } from './lib/sessionMonitoring'
import './index.css'

const sessionMonitoringEnabled = isSessionMonitoringOrigin(window.location.origin)
// Install the capture shim and initialize FullStory before application rendering or startup fetches.
// Preview, branch, and local deploys must not capture user sessions even when they inherit production
// build-time configuration.
if (sessionMonitoringEnabled) initFullStory()

// Instrument every button and input in the app with analytics. Pageviews are tracked per-route
// inside <App>; this wires the document-level click/input listeners once.
captureAdAttribution()
// Load GA4 gtag so Google Ads conversions attribute to the gclid (prod origin only; see lib/gtag).
initGtag()
initAutoInstrumentation()
// Report uncaught exceptions and unhandled promise rejections to the admin errors view.
initGlobalErrorTracking()
setupListeners(store.dispatch)

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        {/* reducedMotion="user" disables the journey hero/lightbox layout animations for users
            who prefer reduced motion. */}
        <MotionConfig reducedMotion="user">
          <ErrorBoundary
            regionLabel="The app"
            onError={(error, info) => {
              // React render/lifecycle crashes are caught here rather than by window.onerror, so
              // report them to the admin errors view too. Best-effort; never throw out of onError.
              try {
                trackError(
                  'react-error-boundary',
                  {
                    message: error.message,
                    stack: error.stack?.slice(0, 2000) ?? null,
                    componentStack: info.componentStack?.slice(0, 1000) ?? null,
                  },
                  'app-error-boundary',
                )
              } catch {
                /* error reporting must never throw */
              }
            }}
          >
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </ErrorBoundary>
        </MotionConfig>
      </BrowserRouter>
    </Provider>
  </StrictMode>,
)
